import {
  setUserId,
  setUserProperties as firebaseSetUserProperties,
  logEvent as firebaseLogEvent,
} from 'firebase/analytics'
import { analytics } from '../firebase'
import { isDev } from 'config'
import {
  ActivityType,
  BooleanString,
  NavMenuLogKey,
  PostLocation,
  PostType,
  SNSType,
  UserFollowLogKey,
  UserProfileOpenLogKey,
} from '../_types'
import { setUserProperties as snowplowSetUserProperties } from './snowplow/snowplow'

export const setUserProperties = (
  params: Partial<{
    gender: number
    is_vip: BooleanString
    user_age: number
    user_id: number
    followers_count: number
    followings_count: number
    created_at: number
    last_loggedin_at: number
    status: 'normal' | 'temporary_ban' | 'ban' | 'not_found'
    reported_count: number
    country_code: 'JP' | 'TH' | 'ID' | ''
    language_code: 'ja' | 'th' | 'id' | 'en'
    login_streak_count: number
    age_verified: boolean
  }>
) => {
  setUserId(analytics, (params.user_id ?? '').toString())

  firebaseSetUserProperties(analytics, {
    ...params,
    sex: params.gender === 0 ? 'male' : params.gender === 1 ? 'female' : 'none',
  })

  snowplowSetUserProperties(params)
}

const _logEvent = (eventName: string, params?: Object) =>
  firebaseLogEvent(
    analytics,
    eventName,
    isDev ? { ...params, debug_event: 1 } : params
  )

export const logEvent = {
  account_sign_up: (params: { type: SNSType }) =>
    _logEvent('account_sign_up', params),

  account_log_in: (params: {
    type: SNSType
    from: 'first_view' | 'setting' | 'profile_tab'
  }) => _logEvent('account_log_in', params),

  account_log_out: () => _logEvent('account_log_out'),

  chat_message_send: (params: {
    type: 'text' | 'image' | 'sticker' | 'gif' | 'video'
    target_user_id: string
    target_chat_room_id: string
  }) => _logEvent('chat_message_send', params),

  chat_room_start: () => _logEvent('chat_room_start'),

  chat_room_create: () => _logEvent('chat_room_create'),

  chat_room_open: (params: {
    from:
      | 'profile'
      | 'himanow'
      | 'post'
      | 'chat_list'
      | 'chat_invite'
      | 'push_notification'
      | 'online_following'
      | 'user_search'
      | 'contact_friends'
      | 'twitter_friends'
      | 'follow_recommendations'
      | 'followers_you_know'
      | 'profile_follow_recommendations'
      | 'group_members_you_know'
      | 'call_members'
      | 'hidden_chat_list'
    target_chat_room_id: string
  }) => _logEvent('chat_room_open', params),

  chat_room_invite: (params: { target_chat_room_id: string }) =>
    _logEvent('chat_room_invite', params),

  gift_select: (params: {
    from: 'call' | 'group'
    from_id: number
    gift_id: number
  }) => _logEvent('gift_select', params),

  gift_send: (params: {
    from: 'call' | 'group'
    from_id: number
    gift_id: number
    receiver_user_id: number
    call_duration_seconds?: number
  }) => _logEvent('gift_send', params),

  gift_error: (params: {
    type: 'gift_send' | 'coin_purchase'
    error_code: number
  }) => _logEvent('gift_error', params),

  gift_history_gift_item_details_open: (params: { gift_id: number }) =>
    _logEvent('gift_history_gift_item_details_open', params),

  group_create: () => _logEvent('group_create'),

  group_join: (params: {
    target_group_id: string
    target_group_type: 'public' | 'private'
  }) => _logEvent('group_join', params),

  group_search: (params: {
    keyword: string
    group_category_id?: number
    sub_category_id?: number
  }) => _logEvent('group_search', params),

  group_search_result_accept: (params: {
    source: 'Participating' | 'All' | 'Search'
    result_position: number
    group_id: number
    is_joined: BooleanString
  }) => _logEvent('group_search_result_accept', params),

  group_category_select: (params: { group_category_id: number }) =>
    _logEvent('group_category_select', params),

  group_leave: (params: { group_id: number; group_category_id: number }) =>
    _logEvent('group_leave', params),

  group_delete: (params: { group_id: number; group_category_id: number }) =>
    _logEvent('group_delete', params),

  group_tab_change: (params: { from_tab: string; to_tab: string }) => {
    return _logEvent('group_tab_change', {
      action: 'click',
      ...params,
    })
  },

  navigation_pane_select: (params: { destination: NavMenuLogKey }) =>
    _logEvent('navigation_pane_select', params),

  private_call_start: (params: { target_chat_room_id: string }) =>
    _logEvent('private_call_start', params),

  private_call_end: (params: {
    duration: string
    target_chat_room_id: string
  }) => _logEvent('private_call_end', params),

  group_call_start: (params: {
    is_owner: BooleanString
    target_post_id: string
  }) => _logEvent('group_call_start', params),

  group_call_end: (params: {
    duration: string
    is_owner: BooleanString
    participant_count: string
    target_post_id: string
  }) => _logEvent('group_call_end', params),

  post_create: (params: {
    type: PostType
    where_to: PostLocation
    target_user_id: string
  }) => _logEvent('post_create', params),

  post_like: (params: { target_user_id: string; target_post_id: string }) =>
    _logEvent('post_like', params),

  post_view: (params: {
    target_user_id: string
    target_post_id: string
    duration_sec: string
    from_timeline: string
    is_hot: BooleanString
  }) => _logEvent('post_view', params),

  timeline_expand: (params: {
    addition_post_count: string
    type?: 'hot_post'
  }) => _logEvent('timeline_expand', params),

  timeline_filter_open: (params: {
    current_hide_hot_posts: BooleanString
    current_no_reply_public_timeline: BooleanString
    current_no_reply_following_timeline: BooleanString
    current_reply_group_timeline: BooleanString
  }) => _logEvent('timeline_filter_open', params),

  timeline_filter_update: (params: {
    hide_hot_posts?: BooleanString
    no_reply_public_timeline?: BooleanString
    no_reply_following_timeline?: BooleanString
    no_reply_group_timeline?: BooleanString
    origin: 'device_setting' | 'timeline'
  }) => _logEvent('timeline_filter_update', params),

  review_send: (params: { target_user_id: string }) =>
    _logEvent('review_send', params),

  user_follow: (params: {
    target_user_id: string
    approval: 'none' | 'requested'
    from: UserFollowLogKey
  }) => _logEvent('user_follow', params),

  user_unfollow: (params: { target_user_id: string; from: UserFollowLogKey }) =>
    _logEvent('user_unfollow', params),

  user_search: (params: {
    nickname: string
    biography: string
    similar_age: BooleanString
    not_recent_gomimushi: BooleanString
    recently_created: BooleanString
    same_prefecture: BooleanString
  }) => _logEvent('user_search', params),

  user_profile_open: (params: {
    target_user_id: string
    from: UserProfileOpenLogKey
  }) => _logEvent('user_profile_open', params),

  subscription_success: (params: { product_id: string }) =>
    _logEvent('subscription_success', params),

  subscription_failed: (params: { product_id: string }) =>
    _logEvent('subscription_failed', params),

  account_delete: () => _logEvent('account_delete'),

  uploaded_contacts_delete: () => _logEvent('uploaded_contacts_delete'),

  gb_experiment_view: (params: {
    experiment_id: string
    variation_id: number
  }) => _logEvent('gb_experiment_view', params),

  thread_convert_start: (params: {
    post_id: number
    group_id: number
    user_role: 'owner' | 'moderator' | 'member'
  }) => _logEvent('thread_convert_start', params),

  thread_convert_confirm: (params: {
    post_id: number
    group_id: number
    is_confirm: BooleanString
  }) => _logEvent('thread_convert_confirm', params),

  thread_convert_successful: (params: {
    thread_id: number
    group_id: number
  }) => _logEvent('thread_convert_successful', params),

  activity_tap: (params: { type: ActivityType }) =>
    _logEvent('activity_tap', params),
}
